/* MARTLI family combinations. No external services, cookies or customer-data storage. */
(() => {
  const adultSize = value => /^(?:[2-5]?X{0,3}[SML])$/.test(String(value).split('(')[0].trim().toUpperCase());
  const childSize = value => /^\d{2,3}$/.test(String(value).trim());
  const childRole = role => ['girl', 'boy', 'child'].includes(role);
  const sizeIndex = p => p.options.findIndex(o => /dyd|size/i.test(typeof o === 'string' ? o : o.name));
  const variantsFor = (p, role) => {
    const si = sizeIndex(p);
    return p.variants.filter(v => {
      if (role !== 'current' && si >= 0 && !(childRole(role) ? childSize(v.options[si]) : adultSize(v.options[si]))) return false;
      const gi = p.options.findIndex(o => /modelis/i.test(typeof o === 'string' ? o : o.name));
      if (gi >= 0 && ['mum', 'dad'].includes(role)) return v.options[gi] === (role === 'mum' ? 'Moterims' : 'Vyrams');
      return true;
    });
  };
  const matchVariant = (variants, values) => values.every(Boolean) ? variants.find(v => v.options.every((o, i) => o === values[i])) : undefined;
  // Styling is curated against the main product photograph, never the first colour option.
  const paletteFor = config => config.photoPalette || config.palette || [];
  const recommendations = (data, handle, currentOptions = [], currentAdult = false) => {
    const source = data.products[handle];
    if (!source) return [];
    const sourceRole = source.adultRoleOption?.values[currentOptions[source.adultRoleOption.index]] || (source.roles.includes('dad') && !source.roles.includes('mum') ? 'dad' : 'mum');
    const roles = currentAdult ? ['boy', 'girl', sourceRole === 'dad' ? 'mum' : 'dad'] : ['mum', 'dad', source.roles.includes('girl') && !source.roles.includes('boy') ? 'boy' : 'girl'];
    const explicit = data.curated?.[handle];
    return roles.flatMap(role => {
      const choices = (explicit || []).filter(r => r.role === role);
      const valid = choices.flatMap(choice => {
        const target = data.products[choice.handle];
        if (!target || !target.roles.includes(role) || (handle === 'maikutes' && target.kind !== 'shirt')) return [];
        return [{...choice, role, same:target.family === source.family, preset:choice.preset || target.photoPreset || {}}];
      });
      return valid.length ? [valid] : [];
    });
  };
  const cartItems = cards => cards.filter(c => c.included).map(c => {
    if (!c.variant?.available) throw new Error('variant');
    const properties = {};
    if (c.needsHeight) {
      const height = Number(String(c.height).replace(',','.'));
      if (!Number.isFinite(height) || height <= 0) throw new Error('height');
      properties['Ūgis (cm)'] = String(height);
    }
    return {id:c.variant.id,quantity:1,...(Object.keys(properties).length ? {properties} : {})};
  });
  const core = {adultSize,childSize,variantsFor,matchVariant,paletteFor,recommendations,cartItems};
  if (typeof module !== 'undefined' && module.exports) {module.exports = core; return;}
  window.MartliFamilyCore=core;
  if (customElements.get('martli-family')) return;
  const productCache = new Map();
  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  class MartliFamily extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.root = window.Shopify?.routes?.root || '/';
      this.cards = []; this.sequence = 0; this.generation = 0; this.busy = false;
      this.texts = JSON.parse(this.querySelector('[data-family-strings]').textContent);
      this.grid = this.querySelector('.mf__grid'); this.form = this.querySelector('form');
      this.button = this.querySelector('.mf__submit'); this.status = this.querySelector('.mf__status');
      this.form.addEventListener('submit', e => {e.preventDefault(); this.submit();});
      (this.dataset.mode === 'look' ? this.startLook() : this.start()).catch(() => {if(this.dataset.mode === 'look'){this.status.textContent=this.texts.load_error;this.button.disabled=true;}else{this.hidden=true;}});
    }
    async startLook() {
      this.status.textContent=this.texts.loading;
      const response=await fetch(this.dataset.catalog);
      if(!response.ok)throw new Error('catalog');
      this.data=await response.json();
      const entries=JSON.parse(this.dataset.entries || '[]');
      const results=await Promise.all(entries.map(async rec=>{
        try {const p=await this.product(rec.handle);return {p,rec};} catch(_){return null;}
      }));
      let missing=false;
      results.forEach(result=>{
        if(!result || !this.data.products[result.p.handle]){missing=true;return;}
        const {p,rec}=result;
        const preset=rec.preset || this.data.products[p.handle].photoPreset || {};
        if(!variantsFor(p,rec.role).some(v=>v.available && Object.entries(preset).every(([i,value])=>v.options[Number(i)]===value))){missing=true;return;}
        this.makeCard(p,{...rec,preset,hideReason:true});
      });
      this.status.textContent=missing || !this.cards.length?this.texts.load_error:'';
      this.hidden=false;this.updateTotal();
    }
    async product(handle) {
      if (!productCache.has(handle)) productCache.set(handle, fetch(this.root + 'products/' + encodeURIComponent(handle) + '.js').then(r => {
        if (!r.ok) throw new Error('product'); return r.json();
      }).catch(e => {productCache.delete(handle); throw e;}));
      return productCache.get(handle);
    }
    money(amount) {return new Intl.NumberFormat(this.dataset.locale || 'lt-LT',{style:'currency',currency:this.dataset.currency || 'EUR'}).format(amount/100);}
    async start() {
      const [data, p] = await Promise.all([fetch(this.dataset.catalog).then(r=>{if(!r.ok)throw new Error('catalog');return r.json();}),this.product(this.dataset.handle)]);
      if (!data.products[p.handle]) return;
      this.data = data; this.source = p;
      if (data.products[p.handle].hideRelated) document.querySelectorAll('product-recommendations.related-products').forEach(block => {block.hidden = true;});
      const current = this.makeCard(p,{handle:p.handle,role:'current',preset:data.products[p.handle].photoPreset || {}});
      this.current = current;
      await this.loadRecommendations();
      document.querySelectorAll('.martli-family-links').forEach(block => {block.hidden = true;});
      if (this.cards.length < 2) return;
      this.hidden = false;
      document.querySelectorAll('.martli-family-jump').forEach(link => {link.hidden = false;});
      document.querySelectorAll('.martli-family-links').forEach(block => {block.hidden = true;});
      this.updateTotal();
    }
    async loadRecommendations() {
      const generation = ++this.generation;
      const si = sizeIndex(this.source);
      const opts = this.current.selects.map(s=>s.value);
      const sourceConfig = this.data.products[this.source.handle];
      const isAdult = si>=0 && opts[si] ? adultSize(opts[si]) : !sourceConfig.roles.some(childRole);
      const lists = recommendations(this.data,this.source.handle,opts,isAdult);
      const results = await Promise.all(lists.map(async list => {
        for (const rec of list) {
          try {
            const p = await this.product(rec.handle);
            const allowed = variantsFor(p,rec.role);
            const preview = allowed.find(v=>v.available && Object.entries(rec.preset).every(([i,value])=>v.options[Number(i)]===value));
            if (!preview) continue;
            return {p,rec};
          } catch (_) { /* Try the next curated alternative. */ }
        }
      }));
      if (generation !== this.generation || this.cards.some(c=>c!==this.current && c.included)) return;
      this.cards.filter(c=>c!==this.current).forEach(c=>c.node.remove()); this.cards = [this.current];
      results.filter(Boolean).forEach(({p,rec})=>this.makeCard(p,rec));
      this.hidden = this.cards.length < 2;
      document.querySelectorAll('.martli-family-jump').forEach(link => {link.hidden = this.hidden;});
      this.updateTotal();
    }
    makeCard(p, rec, extra=false) {
      const config = this.data.products[p.handle];
      const variants = variantsFor(p,rec.role);
      if (!variants.length) return;
      const id = this.id + '-' + (++this.sequence);
      const card = {p,rec,config,variants,selects:[],included:false,variant:null,height:'',needsHeight:false};
      const node = el('article','mf__card'+(extra?' mf__card--extra':'')); card.node=node;
      const url = this.root+'products/'+encodeURIComponent(p.handle);
      const pictureLink = el('a','mf__image-link'); pictureLink.href=url; pictureLink.tabIndex=-1; pictureLink.setAttribute('aria-hidden','true');
      const img=el('img','mf__image'); img.loading='lazy'; img.decoding='async'; img.width=320; img.height=400; img.alt=p.title;
      const originalImage=p.featured_image || p.images?.[0];
      const imageURL = src => {if(!src)return ''; const u=new URL(src,location.origin);u.searchParams.set('width','420');return u.href;};
      if(originalImage) img.src=imageURL(originalImage); else pictureLink.hidden=true;
      pictureLink.append(img); node.append(pictureLink);
      const body=el('div','mf__body'); node.append(body);
      body.append(el('p','mf__role',this.texts[rec.role]));
      const heading=el('h3','mf__title'); const link=el('a','',p.title); link.href=url; heading.append(link); body.append(heading);
      if(rec.role!=='current' && !rec.hideReason)body.append(el('p','mf__reason',this.texts[rec.same?'same':rec.neutral?'neutral':'matching']));
      const price=el('p','mf__price');body.append(price);
      const pick=el('label','mf__pick'); const check=el('input');check.type='checkbox';check.id=id+'-pick';pick.htmlFor=check.id;
      pick.append(check,el('span','',this.texts.select));body.append(pick);
      const fields=el('fieldset','mf__options');fields.hidden=true;fields.disabled=true;
      const legend=el('legend','visually-hidden',p.title+' – '+this.texts.choose); fields.append(legend);body.append(fields);
      const linkedVariant=rec.role==='current' && !extra ? variants.find(v=>String(v.id)===new URLSearchParams(location.search).get('variant')) : null;
      p.options.forEach((option,i)=>{
        const name=typeof option==='string'?option:option.name;
        const values=[...new Set(variants.map(v=>v.options[i]))];
        const label=el('label','mf__field');label.htmlFor=id+'-option-'+i;
        label.append(el('span','',/dyd|size/i.test(name)?this.texts.size:name));
        const select=el('select');select.id=label.htmlFor;select.required=true;select.name=id+'-option-'+i;
        select.append(new Option(this.texts.choose,''));values.forEach(v=>select.append(new Option(v,v)));
        if(values.length===1)select.value=values[0];
        else if(rec.preset?.[i] && values.includes(rec.preset[i]))select.value=rec.preset[i];
        if(linkedVariant)select.value=linkedVariant.options[i];
        label.append(select);fields.append(label);card.selects.push(select);
      });
      const heightWrap=el('label','mf__field');heightWrap.htmlFor=id+'-height';heightWrap.append(el('span','',this.texts.height));
      const height=el('input');height.type='number';height.inputMode='decimal';height.min='1';height.step='0.1';height.id=heightWrap.htmlFor;
      heightWrap.append(height,el('small','',this.texts.height_help));fields.append(heightWrap);
      const xs=el('p','mf__xs',this.texts.xs);xs.hidden=true;fields.append(xs);
      const note=el('small','mf__image-note',this.texts.image_note);note.hidden=true;fields.append(note);
      const availability=el('p','mf__availability');availability.setAttribute('aria-live','polite');fields.append(availability);
      const detail=el('a','mf__detail',this.texts.details);detail.href=url;fields.append(detail);
      const duplicate=el('button','mf__extra',extra?this.texts.remove:this.texts.duplicate);duplicate.type='button';fields.append(duplicate);
      duplicate.addEventListener('click',()=>{
        if(this.busy)return;
        if(extra){node.remove();this.cards=this.cards.filter(c=>c!==card);this.updateTotal();return;}
        if(this.cards.length>=8)return;
        const another=this.makeCard(p,{...rec,role:rec.role==='current'?'current':rec.role},true);
        if(another){another.check.checked=true;another.check.dispatchEvent(new Event('change'));another.check.focus();}
      });
      card.check=check;card.fields=fields;
      const refresh=()=>{
        const values=card.selects.map(s=>s.value);
        card.variant=matchVariant(variants,values);card.included=check.checked;
        node.classList.toggle('is-selected',card.included);fields.hidden=!card.included;fields.disabled=!card.included;
        const si=sizeIndex(p);card.needsHeight=config.suit && si>=0 && adultSize(values[si]);
        heightWrap.hidden=!card.needsHeight;height.disabled=!card.needsHeight || !card.included;height.required=card.needsHeight && card.included;
        card.height=height.value;
        xs.hidden=!(si>=0 && String(values[si]).split('(')[0].trim()==='XS');
        card.selects.forEach((select,i)=>[...select.options].forEach(o=>{
          o.disabled=Boolean(o.value) && !variants.some(v=>v.available && v.options[i]===o.value && values.every((value,j)=>j===i || !value || v.options[j]===value));
        }));
        const range=variants.filter(v=>v.available && values.every((value,i)=>!value || v.options[i]===value)).map(v=>v.price);
        price.textContent=card.variant ? this.money(card.variant.price) : range.length ? (Math.min(...range)!==Math.max(...range)?this.texts.from+' ':'')+this.money(Math.min(...range)) : this.texts.unavailable;
        availability.textContent=card.variant ? card.variant.available?'':this.texts.sold_out : this.texts.choose_options;
        const previewVariant=card.variant || variants.find(v=>v.available && values.every((value,i)=>!value || v.options[i]===value));
        const variantImage=previewVariant?.featured_image?.src || previewVariant?.featured_image?.url;
        if(card.included && variantImage)img.src=imageURL(variantImage);else if(originalImage)img.src=imageURL(originalImage);
        const colourIndices=Object.keys(config.colours || {}).map(Number);
        note.hidden=Boolean(variantImage) || !colourIndices.some(i=>values[i] && values[i]!==config.photoPreset?.[i]);
        link.href=card.variant?url+'?variant='+card.variant.id:url;detail.href=link.href;
        this.updateTotal();
      };
      check.addEventListener('change',refresh);height.addEventListener('input',refresh);
      card.selects.forEach(s=>s.addEventListener('change',()=>{
        refresh();
        if(card===this.current && !this.cards.some(c=>c!==card && c.included))this.loadRecommendations().catch(()=>{});
      }));
      this.cards.push(card);this.grid.append(node);refresh();return card;
    }
    updateTotal() {
      const selected=this.cards.filter(c=>c.included);
      const ready=selected.length>0 && selected.every(c=>c.variant?.available && (!c.needsHeight || (Number(c.height)>0)));
      this.querySelector('.mf__count').textContent=this.texts.selected+' '+selected.length;
      const complete=selected.every(c=>c.variant?.available);
      this.querySelector('.mf__total').textContent=complete?this.texts.total+' '+this.money(selected.reduce((n,c)=>n+c.variant.price,0)):this.texts.choose_options;
      this.button.disabled=this.busy || !ready;
    }
    async submit() {
      if(this.busy || !this.form.reportValidity())return;
      let items;try{items=cartItems(this.cards);}catch(_){this.status.textContent=this.texts.choose_options;return;}
      if(!items.length)return;
      this.busy=true;this.updateTotal();this.button.textContent=this.texts.adding;this.status.textContent='';
      this.querySelectorAll('input,select,button').forEach(n=>{n.dataset.wasDisabled=String(n.disabled);n.disabled=true;});
      let response;
      try{
        response=await fetch(this.root+'cart/add.js',{method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({items})});
        if(!response.ok){
          // A stock race can partially change a cart; always show its link instead of blindly retrying.
          this.status.textContent=this.texts.error;this.querySelector('.mf__cart').hidden=false;
          return;
        }
        window.location.assign(this.root+'cart');
      }catch(_){this.status.textContent=this.texts.uncertain;this.querySelector('.mf__cart').hidden=false;}
      finally{
        this.querySelectorAll('[data-was-disabled]').forEach(n=>{n.disabled=n.dataset.wasDisabled==='true';delete n.dataset.wasDisabled;});
        this.busy=false;this.button.textContent=this.texts.add;this.updateTotal();
        // Prevent accidental repeated submissions after an uncertain/failed request.
        if(!response?.ok){this.button.disabled=true;this.button.textContent=this.texts.add;}
      }
    }
  }
  customElements.define('martli-family',MartliFamily);
})();
